/**
 * Fetches one property by id (productId) and joins it against the linked
 * loan application, contract, and post-ratification pipeline.
 *
 * For sellers, resolves from SellerContract snapshots by propertyId.
 */

import { useMemo } from 'react';

import { USER_ROLE } from '@/config/constants';
import { useGetAddendumsQuery } from '@/apis/extended/addendumApi';
import { useGetContractQuery } from '@/apis/extended/contractApi';
import {
  useGetLoanApplicationQuery,
  useGetMyLoanApplicationsQuery,
} from '@/apis/extended/loanApplicationApi';
import { useGetContractPipelineQuery } from '@/apis/extended/pipelineApi';
import { useGetProductQuery } from '@/apis/extended/productApi';
import { useGetAllSellerContractsQuery } from '@/apis/extended/sellerAPI';
import { useGetCurrentUserQuery } from '@/apis/extended/userApi';
import { pickApplicationFor } from '@/utils/loanApplicationPick';
import { buildPropertyView } from '@/utils/propertyView/buildPropertyView';
import { normalizeSellerContractRow } from '@/utils/sellerContract/normalizeSellerProduct';
import type { Product } from '@/types/ProductType';
import type { PropertyView } from '@/types/PropertyView';
import type { SellerContractRecord } from '@/types/SellerContractType';

function extractContractId(product?: Product): string | undefined {
  if (!product?.contract) return undefined;
  if (typeof product.contract === 'string') return product.contract;
  return product.contract._id;
}

function findSellerRow(
  rows: SellerContractRecord[] | undefined,
  propertyId: string
): SellerContractRecord | undefined {
  if (!rows?.length) return undefined;
  return rows.find((row) => {
    const { product } = normalizeSellerContractRow(row);
    return product._id === propertyId;
  });
}

export function useProperty(productId: string | undefined) {
  const { data: user } = useGetCurrentUserQuery();
  const isSeller = user?.role === USER_ROLE.SELLER;

  const { data: sellerContracts, isLoading: sellerContractsLoading } =
    useGetAllSellerContractsQuery(user?._id ?? '', {
      skip: !isSeller || !user?._id,
    });

  const sellerRow = useMemo(
    () =>
      isSeller && productId
        ? findSellerRow(
            sellerContracts as unknown as SellerContractRecord[] | undefined,
            productId
          )
        : undefined,
    [isSeller, productId, sellerContracts]
  );

  const sellerNormalized = useMemo(() => {
    if (!sellerRow) return undefined;
    return normalizeSellerContractRow(sellerRow);
  }, [sellerRow]);

  const {
    data: fetchedProduct,
    isLoading: productLoading,
    error: productError,
  } = useGetProductQuery(productId ?? '', {
    skip: !productId || isSeller,
  });

  const product = isSeller ? sellerNormalized?.product : fetchedProduct;

  const { data: applications, isLoading: appsLoading } =
    useGetMyLoanApplicationsQuery(undefined, { skip: isSeller });

  const candidateApp = useMemo(() => {
    if (!product || isSeller) return undefined;
    return pickApplicationFor(product, applications);
  }, [product, applications, isSeller]);

  const { data: application, isLoading: appLoading } =
    useGetLoanApplicationQuery(candidateApp?._id ?? '', {
      skip: isSeller || !candidateApp?._id,
    });

  const contractId = isSeller
    ? sellerNormalized?.contractId
    : extractContractId(product);

  const { data: contract, isLoading: contractLoading } = useGetContractQuery(
    contractId ?? '',
    { skip: !contractId }
  );

  const liveContract = contract ?? sellerNormalized?.contract;

  const { data: addendumsData } = useGetAddendumsQuery(contractId ?? '', {
    skip: !contractId || !isSeller,
  });

  const openAddendumCount = useMemo(() => {
    if (!isSeller) return 0;
    const list = addendumsData?.addendums ?? addendumsData ?? [];
    const arr = Array.isArray(list) ? list : [];
    return arr.filter(
      (a: { status?: string }) =>
        a.status !== 'completed' && a.status !== 'rejected'
    ).length;
  }, [isSeller, addendumsData]);

  const { data: pipeline, isLoading: pipelineLoading } =
    useGetContractPipelineQuery(contractId ?? '', {
      skip: !contractId || !liveContract?.isRatified,
    });

  const property = useMemo<PropertyView | undefined>(() => {
    if (!product) return undefined;
    return buildPropertyView({
      product,
      application: isSeller ? null : application ?? candidateApp,
      contract: liveContract,
      pipeline,
      user,
      pipelineMode: isSeller ? 'seller' : 'buyer',
      contractIdOverride: contractId,
      openAddendumCount,
    });
  }, [
    product,
    isSeller,
    application,
    candidateApp,
    liveContract,
    pipeline,
    user,
    contractId,
    openAddendumCount,
  ]);

  return {
    property,
    product,
    application: isSeller ? undefined : application ?? candidateApp,
    contract: liveContract,
    pipeline,
    user,
    contractId,
    isLoading: isSeller
      ? sellerContractsLoading || contractLoading || pipelineLoading
      : productLoading ||
        appsLoading ||
        appLoading ||
        contractLoading ||
        pipelineLoading,
    error: isSeller ? undefined : productError,
  };
}
